import { useMemo, useState } from "react";
import {
  HStack,
  Text,
  Button,
  Stack,
  StackSeparator,
  Separator,
  Image,
  Card,
  Field,
  NativeSelect,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { Comment } from "../Comment";
import { t } from "i18next";
import { NoCommentsAlert } from "../NoCommentsAlert";
import { AddGeneralComment } from "./AddGeneralComment";
import {
  CommentFilterOption,
  filterCommentByOption,
} from "../../utils/comments-utils";
import { OsaComment } from "../../models/OsaComment";
import { OsaNode } from "../../../osaway/models/OsaNode";
import { useStores } from "../../../shared/stores/RootStore";
import { CustomDialog } from "../../../shared/views/CustomDialog";

type nodeId = string;
interface Props {
  onClose: () => void;
}

const CommentsDashboardModalBase: React.FC<Props> = ({ onClose }) => {
  const { mapStore, osaWayStore, osaNodeStore, osaCommentStore } =
    useStores();
  const [filterOption, setFilterOption] = useState(
    CommentFilterOption.Unresolved
  );
  const osaWay = osaWayStore.getSelected()!;
  const nodes = osaNodeStore.nodesByWayId.get(osaWay.id)!;

  const [wayComments, nodeComments, nodesByIndex] = useMemo(() => {
    const comments = osaCommentStore.commentsByOsaWayId.get(osaWay.id) ?? [];

    const filteredComments = comments.filter((comment) =>
      filterCommentByOption(comment, filterOption)
    );

    const wayComments: OsaComment[] = [];
    const nodeComments = new Map<nodeId, OsaComment[]>();

    filteredComments.forEach((comment) => {
      if (!comment.osaNodeId) {
        wayComments.push(comment);
      } else {
        const prevNodeComments = nodeComments.get(comment.osaNodeId);
        if (prevNodeComments) {
          prevNodeComments.push(comment);
        } else {
          nodeComments.set(comment.osaNodeId, [comment]);
        }
      }
    });

    const nodeIndexes: number[] = [];

    for (const nodeId of nodeComments.keys()) {
      const nodeIndex = nodes.findIndex((node) => node.id === nodeId);

      nodeIndexes.push(nodeIndex);
    }

    nodeIndexes.sort();
    const nodesByIndex = new Map<number, OsaNode>();

    nodeIndexes.forEach((nodeIndex) => {
      nodesByIndex.set(
        nodeIndex,
        osaNodeStore.nodesByWayId.get(osaWay.id)![nodeIndex]
      );
    });

    return [wayComments, nodeComments, nodesByIndex];
    // eslint-disable-next-line
  }, [osaCommentStore.commentsByOsaWayId, filterOption]);

  return (
    <CustomDialog
      size="xl"
      onClose={onClose}
      header={t(`map:comments.commentDashboard`)}
    >
      <Stack gap={4}>
        <Field.Root mb={4}>
          <Field.Label>{t(`map:comments.filterComments`)}</Field.Label>
          <NativeSelect.Root>
            <NativeSelect.Field
              value={filterOption}
              onChange={(e) =>
                setFilterOption(e.target.value as CommentFilterOption)
              }
            >
              <option value={CommentFilterOption.Unresolved}>
                {t(
                  `map:filters.commentOptions.${CommentFilterOption.Unresolved}`
                )}
              </option>
              <option value={CommentFilterOption.Resolved}>
                {t(
                  `map:filters.commentOptions.${CommentFilterOption.Resolved}`
                )}
              </option>
              <option value={CommentFilterOption.All}>
                {t(`map:filters.commentOptions.${CommentFilterOption.All}`)}
              </option>
            </NativeSelect.Field>
          </NativeSelect.Root>
        </Field.Root>

        <Separator />

        <Text fontSize="lg" fontWeight="medium">
          {t("map:wayComments.title")}
        </Text>
        <Stack gap={4} separator={<StackSeparator />}>
          {wayComments.map((comment, i) => (
            <Comment comment={comment} key={i} />
          ))}
          {!wayComments.length && <NoCommentsAlert />}
        </Stack>

        {filterOption !== CommentFilterOption.Resolved && (
          <AddGeneralComment osaWayId={osaWay.id} />
        )}

        <Separator />
        <Text fontSize="lg" fontWeight="medium">
          {t("map:nodesList.nodeComments")}
        </Text>
        <Stack gap={4} separator={<StackSeparator />}>
          {!nodesByIndex.size && <NoCommentsAlert />}
          {[...nodesByIndex.entries()].map(([nodeIndex, node], i) => (
            <HStack
              gap={4}
              key={i}
              alignItems="stretch"
              separator={<StackSeparator />}
            >
              <Stack w="30%" flexShrink={0}>
                <Text fontSize="sm" fontWeight="semibold">
                  {t("map:nodesList.titleSmall", {
                    index: nodeIndex,
                    total: nodes.length,
                  })}
                </Text>
                <Card.Root overflow="hidden">
                  <Image src={node.photo?.url} alt="" />
                </Card.Root>
                <Button
                  size="sm"
                  colorPalette="primary"
                  onClick={() => {
                    osaNodeStore.select(node.id);
                    mapStore.recenterMap();
                    onClose();
                  }}
                >
                  {t("map:nodesList.viewNode")}
                </Button>
              </Stack>
              <Stack flexGrow={1} gap={4} separator={<StackSeparator />}>
                {nodeComments.get(node.id)!.map((comment, i) => (
                  <Comment comment={comment} key={i} />
                ))}
              </Stack>
            </HStack>
          ))}
        </Stack>
      </Stack>
    </CustomDialog>
  );
};

export const CommentsDashboardModal = observer(CommentsDashboardModalBase);
